import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import '../styles/ListaReservasPendientes.css';
import useReservasPendientes from '../hooks/useReservasPendientes';
import Spinner from './Spinner';
import DocumentViewer from './DocumentViewer';
import { formatearFechaPlano, formatearHora, urlApi } from '../helpers/functionHelpers';

const ListaReservasPendientes = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredReservas, setFilteredReservas] = useState([]);
  const [selectedFolio, setSelectedFolio] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;
  const { reservas, loading: loadingReservas, error: errorReservas, actualizarReservas } = useReservasPendientes();

  const handleSearchChange = (e) => {
    const value = e.target.value.toLowerCase();
    setSearchTerm(value);
  
    const filtered = reservas.filter((reserva) => {
      const folio = reserva.folio?.toLowerCase() || '';
      const insumos = reserva.insumos?.toLowerCase() || '';
      const compania = reserva.compania?.toLowerCase() || '';
      const nombrePrv = reserva.nombrePrv?.toLowerCase() || '';
  
      return (
        folio.includes(value) ||
        insumos.includes(value) ||
        compania.includes(value) ||
        nombrePrv.includes(value)
      );
    });
    const ordenadas = filtered.sort((a, b) => new Date(b.fechaRsv) - new Date(a.fechaRsv));
    setFilteredReservas(ordenadas);
    setCurrentPage(1); // Reiniciar paginador al buscar
  };

  useEffect(() => {
    const ordenadas = [...reservas].sort((a, b) => new Date(b.fechaRsv) - new Date(a.fechaRsv));
    setFilteredReservas(ordenadas);
  }, [reservas]);

  const archivarCita = async (folio) => {
    if (!window.confirm(`¿Archivar la cita con folio ${folio}? Ya no aparecerá en Pendientes.`)) return;
    try {
      const token = localStorage.getItem('auth_token');
      await fetch(`${urlApi}/api/proveedor/${folio}/status`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ nuevoStatusId: 110 }),
      });
      actualizarReservas();
    } catch (err) {
      alert('Error al archivar: ' + err.message);
    }
  };
  
  if (loadingReservas) {
    return (
      <div className="loader-container">
        <div className="spinner-wrapper">
          <Spinner />
          <p className="loading-text">Cargando datos...</p>
        </div>
      </div>
    );
  }

  if (errorReservas) {
    return <p>Error: {errorReservas}</p>;
  }

  return (
    <div className="lista-reservas-container">
      <h1>Reservaciones pendientes (Agrupadas por folio)</h1>
      <input
        type="text"
        placeholder="Buscar por Folio, Proveedor, Insumos, Compañía"
        className="search-bar"
        value={searchTerm}
        onChange={handleSearchChange}
      />
      <button
        className="actualizar-button"
        onClick={actualizarReservas}
        disabled={loadingReservas}
      >
        {loadingReservas ? 'Cargando...' : 'Actualizar'}
      </button>
      <table className="reservas-table">
        <thead>
          <tr>
            <th>Folio</th>
            <th>Proveedor</th>
            <th>OCs</th>
            <th>Insumos</th>
            <th>Status</th>
            <th>Fecha</th>
            <th>Horario</th>
            <th>Documentos</th>
            <th>Archivar</th>
          </tr>
        </thead> 
        <tbody>
          {filteredReservas.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage).map((reserva) => (
            <React.Fragment key={reserva.folio}>
              <tr className="clickable-row">
                <td>
                  <Link to={`/admin/pendientes/${reserva.folio}`} className="folio-link full-width-link">
                    {reserva.folio}
                  </Link>
                </td>
                <td>
                  <Link to={`/admin/pendientes/${reserva.folio}`} className="full-width-link">
                    {reserva.nombrePrv}
                  </Link>
                </td>
                <td>
                  <span className="badge-ocs" style={{ display: "inline-block", background: "#2196F3", color: "white", padding: "4px 12px", borderRadius: "20px", fontWeight: "bold", fontSize: "12px" }}>
                    {reserva.total_ocs} OC{reserva.total_ocs > 1 ? 's' : ''}
                  </span>
                </td>
                <td>
                  <Link to={`/admin/pendientes/${reserva.folio}`} className="full-width-link" title={reserva.insumos}>
                    {reserva.insumos && reserva.insumos.length > 50 ? `${reserva.insumos.substring(0, 50)}...` : reserva.insumos}
                  </Link>
                </td>
                <td>
                  <Link to={`/admin/pendientes/${reserva.folio}`} className="full-width-link">
                    {reserva.statuses_id === 20 ? 'Pendiente' : `Status #${reserva.statuses_id}`}
                  </Link>
                </td>
                <td>
                  <Link to={`/admin/pendientes/${reserva.folio}`} className="full-width-link">
                    {formatearFechaPlano(reserva.fechaRsv)}
                  </Link>
                </td>
                <td>
                  <Link to={`/admin/pendientes/${reserva.folio}`} className="full-width-link">
                    {reserva.dataIni && reserva.dataFin ? `${formatearHora(reserva.dataIni)} - ${formatearHora(reserva.dataFin)}` : '-'}
                  </Link>
                </td>
                <td>
                  <button 
                    className="expand-button"
                    onClick={() => setSelectedFolio(selectedFolio === reserva.folio ? null : reserva.folio)}
                  >
                    {selectedFolio === reserva.folio ? 'Ocultar' : 'Ver'}
                  </button>
                </td>
                <td>
                  <button
                    className="archive-button"
                    onClick={(e) => { e.stopPropagation(); archivarCita(reserva.folio); }}
                    title="Archivar esta cita"
                  >
                    📦
                  </button>
                </td>
              </tr>
              {selectedFolio === reserva.folio && (
                <tr>
                  <td colSpan="9" className="document-cell">
                    <DocumentViewer folio={reserva.folio} />
                  </td>
                </tr>
              )}
            </React.Fragment>
          ))}
        </tbody>
      </table>
      
      {/* Controles de Paginación */}
      {filteredReservas.length > itemsPerPage && (
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', marginTop: '20px', gap: '15px' }}>
          <button 
            onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
            disabled={currentPage === 1}
            style={{ padding: '8px 16px', border: '1px solid #ccc', borderRadius: '4px', background: currentPage === 1 ? '#f5f5f5' : '#fff', cursor: currentPage === 1 ? 'not-allowed' : 'pointer' }}
          >
            Anterior
          </button>
          
          <span style={{ fontSize: '14px', fontWeight: 'bold', color: '#555' }}>
            Página {currentPage} de {Math.ceil(filteredReservas.length / itemsPerPage)}
          </span>

          <button 
            onClick={() => setCurrentPage(prev => Math.min(prev + 1, Math.ceil(filteredReservas.length / itemsPerPage)))}
            disabled={currentPage === Math.ceil(filteredReservas.length / itemsPerPage)}
            style={{ padding: '8px 16px', border: '1px solid #ccc', borderRadius: '4px', background: currentPage === Math.ceil(filteredReservas.length / itemsPerPage) ? '#f5f5f5' : '#fff', cursor: currentPage === Math.ceil(filteredReservas.length / itemsPerPage) ? 'not-allowed' : 'pointer' }}
          >
            Siguiente
          </button>
        </div>
      )}
    </div>
  );
};

export default ListaReservasPendientes;
